import { useNavigate } from 'react-router-dom'
import { CircleUserRound, ChevronRight } from 'lucide-react'
import AppSidebar from '@/components/AppSidebar'
import { useTranslation } from '@/i18n'

interface WizardLayoutProps {
  step: 1 | 2 | 3 | 4
  totalSteps?: number
  /** Hide the step-progress bar — for single-step flows (editing) where "1 of 4" is meaningless. */
  showProgress?: boolean
  /** Override the trailing breadcrumb label (defaults to "New Digital Coworker"). */
  breadcrumbLabel?: string
  children: React.ReactNode
}

export default function WizardLayout({ step, totalSteps = 4, showProgress = true, breadcrumbLabel, children }: WizardLayoutProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const progress = (step / totalSteps) * 100

  return (
    <div className="flex h-screen">
      <AppSidebar />
      <div className="flex flex-1 flex-col min-w-0">
        {/* Breadcrumb header (replaces AppTopbar) */}
        <header className="flex items-center gap-2 border-b border-[#e5e5e5] bg-white px-6 py-4 text-sm dark:border-dark-border dark:bg-dark-card">
          <button
            type="button"
            onClick={() => navigate('/digital-coworker')}
            className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
          >
            <CircleUserRound className="size-4" />
            {t('digital_coworker:breadcrumb.list', 'Digital Coworker')}
          </button>
          <ChevronRight className="size-4 text-muted-foreground" />
          <span className="font-medium text-foreground">
            {breadcrumbLabel ?? t('digital_coworker:breadcrumb.new', 'New Digital Coworker')}
          </span>
        </header>

        {/* Teal progress bar */}
        {showProgress && (
          <div className="h-1 bg-muted" data-testid="wizard-progress">
            <div
              className="h-full bg-[#0e7490] transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-white dark:bg-dark-card">
          <div className="mx-auto max-w-[540px] py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
